/**
 * Loading Overlay Component
 * Semi-transparent overlay with spinner for refresh states
 *
 * Used when content is being refreshed (stale-while-revalidate pattern)
 * Shows existing content with overlay, indicating data is being updated
 */

import { Box, Spinner, Text } from '@shopify/polaris';

interface LoadingOverlayProps {
  /**
   * Whether the overlay is visible
   */
  isVisible: boolean;

  /**
   * Optional message to show below spinner
   */
  message?: string;

  /**
   * Accessibility label for the spinner
   * @default 'Refreshing content'
   */
  accessibilityLabel?: string;
}

/**
 * Semi-transparent overlay with centered spinner
 * Uses CSS class from motion.css for smooth transitions
 *
 * @example
 * ```tsx
 * <div style={{ position: 'relative' }}>
 *   <ActualContent />
 *   <LoadingOverlay isVisible={isRefreshing} message="Updating..." />
 * </div>
 * ```
 */
export function LoadingOverlay({
  isVisible,
  message,
  accessibilityLabel = 'Refreshing content',
}: LoadingOverlayProps) {
  if (!isVisible) return null;

  return (
    <div className="loading-overlay" aria-busy="true" aria-live="polite">
      <div className="loading-overlay-content">
        <Spinner size="small" accessibilityLabel={accessibilityLabel} />
        {message && (
          <Box paddingBlockStart="200">
            <Text as="span" variant="bodySm" tone="subdued">
              {message}
            </Text>
          </Box>
        )}
      </div>
    </div>
  );
}

export type { LoadingOverlayProps };
